import React, { useCallback, useRef } from "react";
import { clientStore } from "../lib/clientStorage";
import {
  ACTIVE_PERSISTENCE_USER_KEY,
  getPersistenceGeneration,
  hasPersistenceInvalidationFence,
  persistenceWriteAllowed,
  PERSISTENCE_LOCAL_CLEAR_KEY,
  PERSISTENCE_LOGOUT_KEY,
  readPersistenceEvent,
} from "../lib/persistenceIdentity";
import {
  readRegisteredValue,
  isSearchResultSnapshot,
  SEARCH_DRAFT_POLICY,
  SEARCH_RESULT_POLICY,
  serializeRegisteredValue,
  utf8Bytes,
  valueFitsPolicy,
} from "../lib/persistenceRegistry";
import {
  markPersistenceMutation,
  registerPersistenceFlushParticipant,
  type PersistenceFlushReceipt,
} from "../lib/persistenceReloadBarrier";

const DRAFT_VERSION = SEARCH_DRAFT_POLICY.version;
const LEGACY_DRAFT_VERSION = 1;
const LEGACY_DRAFT_PARSE_LIMIT_BYTES = 5 * 1024 * 1024;

export interface SearchDraft {
  version: number;
  timestamp: number;
  ownerGeneration?: number;
  keyGeneration?: number;
  clientId: string | null;
  clientFirstName?: string;
  clientLastName?: string;
  nearAddress?: string;
  hasExecutedSearch: boolean;
  /** Result rows are returned with the draft but stored under a separate policy. */
  cachedSearchResults?: unknown[];
  cachedResultCriteriaFingerprint?: string;
  filters: {
    priceRange: [number, number];
    selectedBedrooms: string[];
    minSqft: number | "";
    availableOnly: boolean;
    moveInDate: string | null;
    sortBy: string;
    selectedNeighborhoods: string[];
    selectedAmenities: string[];
    selectedManagement: string[];
    maxPricePerSqft: number | "";
    noFloorPlanImage: boolean;
  };
  selectionMode: "floorplan" | "unit";
  selectedFloorPlanKeys: string[];
  selectedUnitKeys: string[];
  emailDialog?: {
    open: boolean;
    subject: string;
    message: string;
  };
  sendDialog?: {
    open: boolean;
    subject: string;
    message: string;
    recipientEmail: string;
  };
}

type SearchDraftInput = Omit<
  SearchDraft,
  "version" | "timestamp" | "ownerGeneration" | "keyGeneration"
>;

export type SearchCriteriaSnapshot = Pick<SearchDraft, "filters" | "nearAddress">;
interface PersistedSearchResultSnapshot {
  criteriaFingerprint: string;
  rows: unknown[];
}

export function searchCriteriaFingerprint(
  criteria: SearchCriteriaSnapshot
): string {
  const sorted = (values: string[]) => [...values].sort();
  return JSON.stringify({
    nearAddress: (criteria.nearAddress ?? "").trim(),
    filters: {
      ...criteria.filters,
      selectedBedrooms: sorted(criteria.filters.selectedBedrooms),
      selectedNeighborhoods: sorted(criteria.filters.selectedNeighborhoods),
      selectedAmenities: sorted(criteria.filters.selectedAmenities),
      selectedManagement: sorted(criteria.filters.selectedManagement),
    },
  });
}

function isSearchDraft(value: unknown): value is SearchDraft {
  if (!value || typeof value !== "object") return false;
  const draft = value as Partial<SearchDraft>;
  const filters = draft.filters;
  const strings = (candidate: unknown) =>
    Array.isArray(candidate) && candidate.every((item) => typeof item === "string");
  const numberOrEmpty = (candidate: unknown) =>
    candidate === "" || (typeof candidate === "number" && Number.isFinite(candidate) && candidate >= 0);
  const canonicalDateOrNull = (candidate: unknown) => {
    if (candidate === null) return true;
    if (typeof candidate !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(candidate)) {
      return false;
    }
    const parsed = new Date(`${candidate}T00:00:00.000Z`);
    return Number.isFinite(parsed.getTime()) &&
      parsed.toISOString().slice(0, 10) === candidate;
  };
  const dialog = (
    candidate: SearchDraft["emailDialog"] | SearchDraft["sendDialog"] | undefined,
    includeRecipient: boolean
  ) => candidate === undefined || Boolean(candidate) &&
    typeof candidate!.open === "boolean" &&
    typeof candidate!.subject === "string" &&
    typeof candidate!.message === "string" &&
    (!includeRecipient ||
      ("recipientEmail" in candidate! && typeof candidate!.recipientEmail === "string"));
  return (draft.version === DRAFT_VERSION || draft.version === LEGACY_DRAFT_VERSION) &&
    Number.isFinite(draft.timestamp) &&
    (draft.version === LEGACY_DRAFT_VERSION ||
      (Number.isSafeInteger(draft.ownerGeneration) &&
        Number.isSafeInteger(draft.keyGeneration))) &&
    (draft.clientId === null || typeof draft.clientId === "string") &&
    (draft.clientFirstName === undefined || typeof draft.clientFirstName === "string") &&
    (draft.clientLastName === undefined || typeof draft.clientLastName === "string") &&
    (draft.nearAddress === undefined ||
      (typeof draft.nearAddress === "string" && draft.nearAddress.length <= 500)) &&
    typeof draft.hasExecutedSearch === "boolean" && Boolean(filters) &&
    Array.isArray(filters?.priceRange) && filters!.priceRange.length === 2 &&
    filters!.priceRange.every((price) => typeof price === "number" && Number.isFinite(price) && price >= 0) &&
    filters!.priceRange[0] <= filters!.priceRange[1] &&
    strings(filters?.selectedBedrooms) &&
    filters!.selectedBedrooms.every((bedroom) =>
      ["0", "1", "2", "3+"].includes(bedroom)
    ) &&
    numberOrEmpty(filters?.minSqft) &&
    typeof filters?.availableOnly === "boolean" &&
    canonicalDateOrNull(filters?.moveInDate) &&
    ["price", "sqft", "price_per_sqft"].includes(filters!.sortBy) &&
    strings(filters?.selectedNeighborhoods) &&
    strings(filters?.selectedAmenities) &&
    strings(filters?.selectedManagement) &&
    numberOrEmpty(filters?.maxPricePerSqft) &&
    typeof filters?.noFloorPlanImage === "boolean" &&
    (draft.selectionMode === "floorplan" || draft.selectionMode === "unit") &&
    strings(draft.selectedFloorPlanKeys) &&
    strings(draft.selectedUnitKeys) &&
    dialog(draft.emailDialog, false) &&
    dialog(draft.sendDialog, true) &&
    (draft.cachedSearchResults === undefined ||
      isSearchResultSnapshot(draft.cachedSearchResults)) &&
    (draft.cachedResultCriteriaFingerprint === undefined ||
      (typeof draft.cachedResultCriteriaFingerprint === "string" &&
        draft.cachedResultCriteriaFingerprint.length <= 4096));
}

function getStorageKey(userId: string, clientId: string | null): string {
  return `search_draft:${userId}:${clientId ?? "no-client"}:search`;
}

function getResultStorageKey(userId: string, clientId: string | null): string {
  return `search_result:${userId}:${clientId ?? "no-client"}:search`;
}

/** Exact workspace restore used by a history entry bound to one client key. */
export function loadExactDraftSync(
  currentClientId: string | null,
  userId: string | null
): SearchDraft | null {
  if (!userId) return null;
  try {
    const draft = readDraftKey(getStorageKey(userId, currentClientId), userId);
    return draft?.clientId === currentClientId ? draft : null;
  } catch {
    return null;
  }
}

const RESULT_CACHE_MAX_KEYS = 3;
const RESULT_CACHE_MAX_TOTAL_BYTES = 2 * 1024 * 1024;
const DRAFT_CACHE_MAX_KEYS = 24;
const DRAFT_CACHE_MAX_TOTAL_BYTES = 1536 * 1024;

function pruneDraftCacheForWrite(
  userId: string,
  currentKey: string,
  currentBytes: number,
  ownerGeneration: number
): void {
  const retained: Array<{ key: string; timestamp: number; bytes: number }> = [];
  for (const key of clientStore.keys(`search_draft:${userId}:`)) {
    if (key === currentKey || !key.endsWith(":search")) continue;
    const raw = clientStore.getItem(key);
    try {
      const parsed = raw ? JSON.parse(raw) as SearchDraft : null;
      if (
        !raw || !parsed || !isSearchDraft(parsed) ||
        Date.now() - parsed.timestamp >= SEARCH_DRAFT_POLICY.ttlMs ||
        (parsed.version === DRAFT_VERSION &&
          (parsed.ownerGeneration !== ownerGeneration ||
            parsed.keyGeneration !== clientStore.generation(key)))
      ) {
        clientStore.removeItem(key);
        clientStore.removeItem(key.replace(/^search_draft:/, "search_result:"));
        continue;
      }
      retained.push({ key, timestamp: parsed.timestamp, bytes: utf8Bytes(raw) });
    } catch {
      clientStore.removeItem(key);
      clientStore.removeItem(key.replace(/^search_draft:/, "search_result:"));
    }
  }
  retained.sort((left, right) => left.timestamp - right.timestamp);
  let retainedBytes = retained.reduce((total, entry) => total + entry.bytes, 0);
  while (
    retained.length >= DRAFT_CACHE_MAX_KEYS ||
    retainedBytes + currentBytes > DRAFT_CACHE_MAX_TOTAL_BYTES
  ) {
    const evicted = retained.shift();
    if (!evicted) break;
    retainedBytes -= evicted.bytes;
    clientStore.removeItem(evicted.key);
    clientStore.removeItem(
      evicted.key.replace(/^search_draft:/, "search_result:")
    );
  }
}

function persistResultSnapshot(
  userId: string,
  clientId: string | null,
  criteria: SearchCriteriaSnapshot,
  rows: unknown[],
  timestamp = Date.now()
): boolean {
  const ownerGeneration = getPersistenceGeneration(userId);
  if (!persistenceWriteAllowed(userId, ownerGeneration)) return false;
  const resultKey = getResultStorageKey(userId, clientId);
  const keyGeneration = clientStore.generation(resultKey);
  const snapshot: PersistedSearchResultSnapshot = {
    criteriaFingerprint: searchCriteriaFingerprint(criteria),
    rows,
  };
  const resultRaw = serializeRegisteredValue(
    snapshot,
    SEARCH_RESULT_POLICY,
    timestamp,
    { ownerGeneration, keyGeneration }
  );
  if (!resultRaw) {
    clientStore.removeItem(resultKey);
    return false;
  }

  const retained: Array<{ key: string; timestamp: number; bytes: number }> = [];
  for (const key of clientStore.keys(`search_result:${userId}:`)) {
    if (key === resultKey) continue;
    const raw = clientStore.getItem(key);
    const value = readRegisteredValue<PersistedSearchResultSnapshot>(
      raw,
      SEARCH_RESULT_POLICY,
      Date.now(),
      {
        ownerGeneration,
        keyGeneration: clientStore.generation(key),
      }
    );
    if (!raw || !value) {
      if (raw) clientStore.removeItem(key);
      continue;
    }
    try {
      retained.push({
        key,
        timestamp: Number(JSON.parse(raw).timestamp) || 0,
        bytes: utf8Bytes(raw),
      });
    } catch {
      clientStore.removeItem(key);
    }
  }
  retained.sort((left, right) => left.timestamp - right.timestamp);
  let retainedBytes = retained.reduce((total, entry) => total + entry.bytes, 0);
  while (
    retained.length >= RESULT_CACHE_MAX_KEYS ||
    retainedBytes + utf8Bytes(resultRaw) > RESULT_CACHE_MAX_TOTAL_BYTES
  ) {
    const evicted = retained.shift();
    if (!evicted) break;
    retainedBytes -= evicted.bytes;
    clientStore.removeItem(evicted.key);
  }
  if (!persistenceWriteAllowed(userId, ownerGeneration)) return false;
  return clientStore.setItemGuarded(resultKey, resultRaw, keyGeneration);
}

function persistDraftPayload(
  userId: string,
  draft: SearchDraftInput,
  timestamp = Date.now()
): boolean {
  const {
    cachedSearchResults: _cachedSearchResults,
    cachedResultCriteriaFingerprint: _cachedResultCriteriaFingerprint,
    ...compactDraft
  } = draft;
  const ownerGeneration = getPersistenceGeneration(userId);
  const draftKey = getStorageKey(userId, draft.clientId);
  const keyGeneration = clientStore.generation(draftKey);
  if (!persistenceWriteAllowed(userId, ownerGeneration)) return false;
  const payload: SearchDraft = {
    ...compactDraft,
    version: DRAFT_VERSION,
    timestamp,
    ownerGeneration,
    keyGeneration,
  };
  const draftRaw = JSON.stringify(payload);
  if (!valueFitsPolicy(draftRaw, SEARCH_DRAFT_POLICY)) {
    clientStore.reportMemoryOnly(
      "oversize",
      draftKey
    );
    return false;
  }

  pruneDraftCacheForWrite(
    userId,
    draftKey,
    utf8Bytes(draftRaw),
    ownerGeneration
  );
  if (!persistenceWriteAllowed(userId, ownerGeneration)) return false;

  const draftStored = clientStore.setItemGuarded(
    draftKey,
    draftRaw,
    keyGeneration
  );
  if (!draftStored || !persistenceWriteAllowed(userId, ownerGeneration)) {
    return false;
  }
  // Result snapshots are intentionally independent of the compact work
  // debounce. Reading/parsing an up-to-1 MiB result on every compose edit
  // defeats the split and can introduce main-thread stalls. The component
  // invalidates a result exactly once when executed criteria diverge.
  return draftStored && clientStore.getDurableItem(draftKey) === draftRaw;
}

function readDraftKey(key: string, userId: string): SearchDraft | null {
  const raw = clientStore.getItem(key);
  if (!raw) return null;
  if (utf8Bytes(raw) > LEGACY_DRAFT_PARSE_LIMIT_BYTES) {
    clientStore.removeItem(key);
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isSearchDraft(parsed)) {
      clientStore.removeItem(key);
      return null;
    }
    if (
      parsed.timestamp > Date.now() + 5 * 60 * 1000 ||
      Date.now() - parsed.timestamp >= SEARCH_DRAFT_POLICY.ttlMs
    ) {
      clientStore.removeItem(key);
      clientStore.removeItem(getResultStorageKey(userId, parsed.clientId));
      return null;
    }

    if (parsed.version === LEGACY_DRAFT_VERSION) {
      if (hasPersistenceInvalidationFence(
        userId,
        getPersistenceGeneration(userId)
      )) {
        clientStore.removeItem(key);
        clientStore.removeItem(getResultStorageKey(userId, parsed.clientId));
        return null;
      }
      // Migrate the old combined object into compact work + optional results.
      const {
        version: _version,
        timestamp: _timestamp,
        ownerGeneration: _ownerGeneration,
        keyGeneration: _keyGeneration,
        ...legacyInput
      } = parsed;
      const {
        cachedSearchResults,
        cachedResultCriteriaFingerprint: _legacyFingerprint,
        ...legacyCompact
      } = legacyInput;
      persistDraftPayload(userId, legacyCompact, parsed.timestamp);
      if (
        cachedSearchResults &&
        Date.now() - parsed.timestamp < SEARCH_RESULT_POLICY.ttlMs
      ) {
        persistResultSnapshot(
          userId,
          parsed.clientId,
          legacyCompact,
          cachedSearchResults,
          parsed.timestamp
        );
      }
      return { ...parsed, version: DRAFT_VERSION };
    }

    if (!valueFitsPolicy(raw, SEARCH_DRAFT_POLICY)) {
      clientStore.removeItem(key);
      return null;
    }
    if (
      parsed.ownerGeneration !== getPersistenceGeneration(userId) ||
      parsed.keyGeneration !== clientStore.generation(key)
    ) {
      clientStore.removeItem(key);
      clientStore.removeItem(getResultStorageKey(userId, parsed.clientId));
      return null;
    }
    const resultKey = getResultStorageKey(userId, parsed.clientId);
    const resultRaw = clientStore.getItem(resultKey);
    const resultSnapshot = readRegisteredValue<PersistedSearchResultSnapshot>(
      resultRaw,
      SEARCH_RESULT_POLICY,
      Date.now(),
      {
        ownerGeneration: getPersistenceGeneration(userId),
        keyGeneration: clientStore.generation(resultKey),
      }
    );
    const fingerprint = searchCriteriaFingerprint(parsed);
    const cachedSearchResults = resultSnapshot?.criteriaFingerprint === fingerprint
      ? resultSnapshot.rows
      : null;
    if (resultRaw && !cachedSearchResults) clientStore.removeItem(resultKey);
    return cachedSearchResults
      ? {
          ...parsed,
          cachedSearchResults,
          cachedResultCriteriaFingerprint: fingerprint,
        }
      : parsed;
  } catch {
    clientStore.removeItem(key);
    return null;
  }
}

/** Synchronous first-paint restore, strictly scoped to the authenticated user. */
export function loadDraftSync(
  currentClientId: string | null,
  userId: string | null
): SearchDraft | null {
  if (!userId) return null;
  try {
    const candidates: SearchDraft[] = [];
    for (const key of clientStore.keys(`search_draft:${userId}:`)) {
      if (!key.endsWith(":search")) continue;
      const draft = readDraftKey(key, userId);
      if (draft) candidates.push(draft);
    }
    if (currentClientId !== null) {
      return candidates.find((draft) => draft.clientId === currentClientId) ?? null;
    }
    candidates.sort((a, b) => b.timestamp - a.timestamp);
    return candidates[0] ?? null;
  } catch {
    return null;
  }
}

export function useDraftState(userId: string | null) {
  const userIdRef = useRef<string | null>(userId);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingDraftRef = useRef<SearchDraftInput | null>(null);
  const invalidatedRef = useRef(false);
  const generationRef = useRef(getPersistenceGeneration(userId));
  const [clearRevision, setClearRevision] = React.useState(0);
  userIdRef.current = userId;

  const cancelPending = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = null;
    pendingDraftRef.current = null;
  }, []);

  React.useEffect(() => {
    invalidatedRef.current = false;
    generationRef.current = getPersistenceGeneration(userId);
    return clientStore.subscribe((key, value) => {
      if (key === PERSISTENCE_LOCAL_CLEAR_KEY && value) {
        const event = readPersistenceEvent(value, "local-clear");
        if (event?.userId === userIdRef.current) {
          generationRef.current = event.generation;
          invalidatedRef.current = true;
          cancelPending();
          setClearRevision((revision) => revision + 1);
        }
        return;
      }
      if (key === ACTIVE_PERSISTENCE_USER_KEY && value === userId) {
        const nextGeneration = getPersistenceGeneration(userId);
        if (nextGeneration !== generationRef.current) {
          generationRef.current = nextGeneration;
          if (!invalidatedRef.current) invalidatedRef.current = false;
          cancelPending();
        }
        return;
      }
      if (key !== PERSISTENCE_LOGOUT_KEY || !value) return;
      const event = readPersistenceEvent(value, "logout");
      if (event && (!event.userId || event.userId === userIdRef.current)) {
        invalidatedRef.current = true;
        cancelPending();
      }
    });
  }, [cancelPending, userId]);

  const loadDraft = useCallback(async (
    currentClientId: string | null
  ): Promise<SearchDraft | null> => {
    const uid = userIdRef.current;
    if (!uid) return null;
    if (currentClientId !== null) {
      const exact = readDraftKey(getStorageKey(uid, currentClientId), uid);
      return exact?.clientId === currentClientId ? exact : null;
    }
    return loadDraftSync(null, uid);
  }, []);

  const writeDraftNow = useCallback((draft?: SearchDraftInput): boolean => {
    const pending = draft ?? pendingDraftRef.current;
    const uid = userIdRef.current;
    if (
      !pending || !uid || invalidatedRef.current ||
      !persistenceWriteAllowed(uid, generationRef.current)
    ) {
      return false;
    }
    const stored = persistDraftPayload(uid, pending);
    if (stored && pendingDraftRef.current === pending) {
      pendingDraftRef.current = null;
    }
    return stored;
  }, []);

  const flushPendingDraft = useCallback((): PersistenceFlushReceipt => {
    const pending = pendingDraftRef.current;
    if (!pending) {
      return { participant: "search:draft", dirty: false, durable: true };
    }
    const durable = writeDraftNow(pending);
    return {
      participant: "search:draft",
      dirty: true,
      durable,
      reason: durable ? undefined : "draft-write-unverified",
    };
  }, [writeDraftNow]);

  const saveDraft = useCallback((
    draft: SearchDraftInput,
    immediate = false
  ) => {
    if (invalidatedRef.current) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = null;
    pendingDraftRef.current = draft;
    markPersistenceMutation();
    if (immediate) {
      writeDraftNow();
      return;
    }
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null;
      writeDraftNow();
    }, 500);
  }, [writeDraftNow]);

  const saveResultSnapshot = useCallback((
    clientId: string | null,
    criteria: SearchCriteriaSnapshot,
    rows: unknown[]
  ): boolean => {
    const uid = userIdRef.current;
    if (
      !uid || invalidatedRef.current ||
      !persistenceWriteAllowed(uid, generationRef.current)
    ) return false;
    return persistResultSnapshot(uid, clientId, criteria, rows);
  }, []);

  const discardResultSnapshot = useCallback((clientId: string | null): void => {
    const uid = userIdRef.current;
    if (
      !uid || invalidatedRef.current ||
      !persistenceWriteAllowed(uid, generationRef.current)
    ) return;
    clientStore.removeItem(getResultStorageKey(uid, clientId));
  }, []);

  // A mobile process discard may happen immediately after pagehide. Flush the
  // compact draft synchronously instead of trusting the debounce timer.
  React.useEffect(() => {
    const flush = () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
      flushPendingDraft();
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") flush();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", flush);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", flush);
      flush();
    };
  }, [flushPendingDraft]);

  React.useEffect(
    () => registerPersistenceFlushParticipant("search:draft", flushPendingDraft),
    [flushPendingDraft]
  );

  const clearDraft = useCallback(async (clientId: string | null) => {
    cancelPending();
    const uid = userIdRef.current;
    if (!uid) return;
    clientStore.removeItem(getStorageKey(uid, clientId));
    clientStore.removeItem(getResultStorageKey(uid, clientId));
  }, [cancelPending]);

  const clearAllDrafts = useCallback(async () => {
    cancelPending();
    const uid = userIdRef.current;
    if (!uid) return;
    clientStore.removeByPrefix(`search_draft:${uid}:`);
    clientStore.removeByPrefix(`search_result:${uid}:`);
  }, [cancelPending]);

  const acknowledgeLocalClear = useCallback(() => {
    generationRef.current = getPersistenceGeneration(userIdRef.current);
    invalidatedRef.current = false;
    cancelPending();
  }, [cancelPending]);

  return {
    loadDraft,
    saveDraft,
    saveResultSnapshot,
    discardResultSnapshot,
    clearDraft,
    clearAllDrafts,
    clearRevision,
    acknowledgeLocalClear,
  };
}
